/**
 * UK address autocomplete + resolution via Ideal Postcodes
 * (https://ideal-postcodes.co.uk). Replaces Google Places for the
 * customer-facing address fields — Google was missing a meaningful
 * share of real UK addresses (new-builds, flats, recently-built
 * estates), which silently blocked those customers from completing a
 * booking. Ideal Postcodes is backed by Royal Mail PAF + Multiple
 * Residence, so UK postal-address coverage is complete.
 *
 * Two calls, same shape the rest of the app already expects:
 *   - `searchAddress`   → GET /v1/autocomplete/addresses — typeahead
 *     suggestions (resolution id + display text only, NO coordinates).
 *   - `getPlaceDetails` → GET the hit's udprn/umprn resolution URL —
 *     resolves the picked suggestion to lat/lng + structured address.
 *
 * The key is a *client-side* key: lock it down by allowed URL in the
 * Ideal Postcodes dashboard (https://ideal-postcodes.co.uk/account),
 * the same way the Google key was referrer-restricted. Override per
 * environment with `VITE_IDEAL_POSTCODES_API_KEY`. If a
 * Content-Security-Policy is enforced, allow
 * `https://api.ideal-postcodes.co.uk` in `connect-src`.
 *
 * The professional coverage-area "nearby towns" picker further down
 * still uses the Google Maps SDK (reverse-geocoding a ring of points) —
 * a different problem (towns within a radius) that Ideal Postcodes
 * doesn't solve and Google handles fine. Hence both keys coexist.
 */
const IDEAL_BASE = 'https://api.ideal-postcodes.co.uk';

// Client-side Ideal Postcodes key. Falls back to the provisioned project
// key so the address field works without extra env wiring; override
// per-environment via VITE_IDEAL_POSTCODES_API_KEY.
const IDEAL_API_KEY =
  ((import.meta as any).env?.VITE_IDEAL_POSTCODES_API_KEY as
    | string
    | undefined) || 'ak_mqhzelszveoVWpIZ6ZjchYzqL6XYx';

// Kept ONLY for the nearby-towns SDK path (pro coverage picker) below.
const GOOGLE_API_KEY = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY as
  | string
  | undefined;

export type PlacePrediction = {
  place_id: string;
  description: string; // full human-readable suggestion text
  main_text?: string;
  secondary_text?: string;
};

export type PlaceDetails = {
  latitude?: number;
  longitude?: number;
  postcode?: string;
  city?: string;
  country_code?: string;
  formatted?: string;
};

function requireIdealKey(): string {
  if (!IDEAL_API_KEY) {
    throw new Error(
      'Ideal Postcodes API key is not configured — set VITE_IDEAL_POSTCODES_API_KEY.',
    );
  }
  return IDEAL_API_KEY;
}

function requireGoogleKey(): string {
  if (!GOOGLE_API_KEY) {
    throw new Error(
      'Google Maps API key is not configured — set VITE_GOOGLE_MAPS_API_KEY.',
    );
  }
  return GOOGLE_API_KEY;
}

// PAF post towns come back UPPERCASE ("LONDON"); the rest of the app
// (admin customer list, booking summary) showed Google's proper-case, so
// normalise for display continuity.
function toTitleCase(s: string): string {
  return s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/**
 * Address typeahead via Ideal Postcodes Autocomplete. Returns up to
 * `limit` UK suggestions. Coordinates are NOT included here — call
 * `getPlaceDetails(place_id)` once the user picks one.
 */
export async function searchAddress(
  query: string,
  signal?: AbortSignal,
  limit = 6,
): Promise<PlacePrediction[]> {
  const input = query.trim();
  if (input.length < 3) return [];
  const key = requireIdealKey();

  const url =
    `${IDEAL_BASE}/v1/autocomplete/addresses` +
    `?api_key=${encodeURIComponent(key)}` +
    `&query=${encodeURIComponent(input)}` +
    `&limit=${encodeURIComponent(String(limit))}`;

  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Address search failed (${res.status})`);

  const data = await res.json();
  const hits: any[] = Array.isArray(data?.result?.hits)
    ? data.result.hits
    : [];

  return hits
    .map((h: any): PlacePrediction | null => {
      const suggestion = String(h?.suggestion ?? '').trim();
      // Prefer the udprn resolution URL; fall back to umprn for
      // multiple-residence sub-addresses (flats sharing a delivery point).
      const path: string | undefined = h?.urls?.udprn ?? h?.urls?.umprn;
      if (!suggestion || !path) return null;
      // Ideal Postcodes returns one flat suggestion string; split on the
      // first comma so the dropdown shows a bold first line + muted
      // remainder, matching the previous Google main/secondary layout.
      const comma = suggestion.indexOf(',');
      return {
        place_id: String(path),
        description: suggestion,
        main_text: comma > 0 ? suggestion.slice(0, comma).trim() : suggestion,
        secondary_text:
          comma > 0 ? suggestion.slice(comma + 1).trim() : undefined,
      };
    })
    .filter((p): p is PlacePrediction => !!p);
}

/**
 * Resolve a picked suggestion to coordinates + structured address. The
 * `placeId` is the relative udprn/umprn resolution path the autocomplete
 * hit handed us (e.g. "/v1/udprn/23747771").
 */
export async function getPlaceDetails(
  placeId: string,
  signal?: AbortSignal,
): Promise<PlaceDetails> {
  if (!placeId) return {};
  const key = requireIdealKey();

  const path = placeId.startsWith('http')
    ? placeId
    : `${IDEAL_BASE}${placeId.startsWith('/') ? '' : '/'}${placeId}`;

  const res = await fetch(`${path}?api_key=${encodeURIComponent(key)}`, {
    signal,
  });
  if (!res.ok) throw new Error(`Address lookup failed (${res.status})`);

  const data = await res.json();
  const r = data?.result ?? {};

  const lat = typeof r.latitude === 'number' ? r.latitude : undefined;
  const lng = typeof r.longitude === 'number' ? r.longitude : undefined;
  const town =
    (typeof r.post_town === 'string' && r.post_town.trim()) ||
    (typeof r.dependant_locality === 'string' &&
      r.dependant_locality.trim()) ||
    '';

  // Single-line address from the PAF fields, in Ideal Postcodes' own
  // display order — mirrors the autocomplete `suggestion` text.
  const formatted =
    [r.line_1, r.line_2, r.line_3, r.post_town, r.postcode]
      .filter((p: any) => typeof p === 'string' && p.trim().length > 0)
      .map((p: string) => p.trim())
      .join(', ') || undefined;

  return {
    latitude: lat,
    longitude: lng,
    postcode:
      typeof r.postcode === 'string' && r.postcode.trim()
        ? r.postcode.trim()
        : undefined,
    city: town ? toTitleCase(town) : undefined,
    // Ideal Postcodes is UK-only (Royal Mail PAF) — every result is GB.
    country_code: 'gb',
    formatted,
  };
}

// ============================================================
// Nearby towns (professional coverage-area picker)
// ============================================================
//
// Google has no single "enumerate every town within a radius" call
// (Nearby Search returns businesses/POIs, not localities; Autocomplete
// needs a typed query). So we reverse-geocode a ring of sampled points
// across the radius via the Maps JS SDK Geocoder and collect the
// distinct locality / postal-town names, nearest first. This surfaces
// the major towns the coverage circle touches.
//
// The SDK is loaded lazily — only when a pro reaches the coverage step —
// so customer-facing address fields (which use the REST autocomplete
// above) never pay for it. Reverse geocoding can't go over plain REST
// from the browser (no CORS), which is why this path uses the SDK.

let mapsLoader: Promise<any> | null = null;

function loadGoogleMaps(): Promise<any> {
  const existingApi = (window as any).google?.maps;
  if (existingApi?.Geocoder) return Promise.resolve(existingApi);
  if (mapsLoader) return mapsLoader;
  const key = requireGoogleKey();
  mapsLoader = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key,
    )}`;
    script.onload = () => {
      const api = (window as any).google?.maps;
      if (api?.Geocoder) resolve(api);
      else reject(new Error('Google Maps SDK loaded but Geocoder is unavailable.'));
    };
    script.onerror = () => {
      mapsLoader = null; // allow a retry on next attempt
      reject(new Error('Failed to load Google Maps SDK.'));
    };
    document.head.appendChild(script);
  });
  return mapsLoader;
}

// Point `distMeters` away from (lat,lng) along `bearingDeg` (0 = north).
function destinationPoint(
  lat: number,
  lng: number,
  distMeters: number,
  bearingDeg: number,
): { lat: number; lng: number } {
  const R = 6371000;
  const br = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const dr = distMeters / R;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(dr) +
      Math.cos(lat1) * Math.sin(dr) * Math.cos(br),
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(br) * Math.sin(dr) * Math.cos(lat1),
      Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2),
    );
  return {
    lat: (lat2 * 180) / Math.PI,
    lng: (((lng2 * 180) / Math.PI + 540) % 360) - 180,
  };
}

// Centre + two rings (half + full radius) × 8 bearings = 17 sample
// points. Denser sampling finds more towns at the cost of more geocode
// calls — tune `bearings` / `rings` if needed.
function sampleRingPoints(
  lat: number,
  lng: number,
  radiusMeters: number,
): Array<{ lat: number; lng: number; dist: number }> {
  const pts = [{ lat, lng, dist: 0 }];
  const bearings = 8;
  for (const frac of [0.5, 1]) {
    const d = radiusMeters * frac;
    for (let i = 0; i < bearings; i++) {
      const p = destinationPoint(lat, lng, d, (i * 360) / bearings);
      pts.push({ ...p, dist: d });
    }
  }
  return pts;
}

function extractTown(results: any[]): string | null {
  for (const r of results ?? []) {
    const comps: any[] = r?.address_components ?? [];
    const pick =
      comps.find((c) => (c.types ?? []).includes('postal_town')) ??
      comps.find((c) => (c.types ?? []).includes('locality')) ??
      comps.find((c) => (c.types ?? []).includes('sublocality'));
    if (pick?.long_name) return String(pick.long_name);
  }
  return null;
}

/**
 * Towns / localities within `radiusMeters` of a point, via Google Maps
 * reverse geocoding of sampled ring points. Returns distinct names,
 * nearest first. Same contract as the previous OSM/Overpass helper.
 */
export async function searchNearbyTowns(input: {
  lat: number;
  lng: number;
  radiusMeters: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<string[]> {
  const { lat, lng, radiusMeters, limit = 60 } = input;
  const r = Math.min(Math.max(Math.round(radiusMeters), 1000), 200000);
  const maps = await loadGoogleMaps();
  const geocoder = new maps.Geocoder();

  const points = sampleRingPoints(lat, lng, r);
  const found = await Promise.all(
    points.map(async (pt) => {
      try {
        const resp = await geocoder.geocode({
          location: { lat: pt.lat, lng: pt.lng },
        });
        const name = extractTown(resp?.results ?? []);
        return name ? { name, dist: pt.dist } : null;
      } catch {
        return null;
      }
    }),
  );

  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of found
    .filter((x): x is { name: string; dist: number } => !!x)
    .sort((a, b) => a.dist - b.dist)) {
    const key = f.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(f.name);
    if (out.length >= limit) break;
  }
  return out;
}
